
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Eye, EyeOff } from "lucide-react";

interface LoginFormProps {
  setIsLoading: (loading: boolean) => void;
  setAuthStatus: (status: string | null) => void;
  onLoginSuccess: () => void;
}

export const LoginForm = ({ setIsLoading, setAuthStatus, onLoginSuccess }: LoginFormProps) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const { toast } = useToast();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setAuthStatus(null);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        setAuthStatus(error.message);
      } else {
        toast({
          title: "Innlogget!",
          description: "Du er nå logget inn.",
        });
        onLoginSuccess();
      }
    } catch (error) {
      setAuthStatus("Det oppstod en feil ved innlogging.");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setAuthStatus(null);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/auth?reset=true`,
      });

      if (error) {
        setAuthStatus(error.message);
      } else {
        toast({
          title: "E-post sendt!",
          description: "Sjekk e-posten din for lenke til å tilbakestille passordet.",
        });
        setShowResetForm(false);
        setResetEmail("");
      }
    } catch (error) {
      setAuthStatus("Det oppstod en feil ved sending av tilbakestillingslenke.");
    } finally {
      setIsLoading(false);
    }
  };

  if (showResetForm) {
    return (
      <form onSubmit={handlePasswordReset} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="resetEmail">E-postadresse</Label>
          <Input
            id="resetEmail"
            type="email"
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            required
          />
          <p className="text-sm text-muted-foreground">
            Vi sender deg en lenke for å tilbakestille passordet ditt.
          </p>
        </div>
        <div className="flex gap-2">
          <Button type="submit" className="flex-1">
            Send tilbakestillingslenke
          </Button>
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => setShowResetForm(false)}
          >
            Avbryt
          </Button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={handleLogin} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="email">E-postadresse</Label>
        <Input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="password">Passord</Label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="pr-10"
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <Button type="submit" className="w-full">
          Logg inn
        </Button>
        <Button 
          type="button" 
          variant="link" 
          className="text-sm"
          onClick={() => setShowResetForm(true)}
        >
          Glemt passord?
        </Button>
      </div>
    </form>
  );
};
